import { type AskPunttSearchProgress } from "@mg/schemas/src/christo/askPuntt";
import {
  Buildings,
  CircleNotch,
  ClockCounterClockwise,
  File,
  Globe,
  MagnifyingGlass,
  Ticket,
} from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";

import { TOOL_CHIP_CLASS } from "./AgentSteps";

interface SearchProgressProps {
  searchProgress: AskPunttSearchProgress[];
}

function SourceIcon({ source }: { source: string }) {
  const s = source.toLowerCase();
  const cls = "shrink-0";
  if (s.includes("enterprise"))
    return (
      <Buildings
        size={14}
        className={`${cls} text-puntt-accent-9`}
        aria-hidden
      />
    );
  if (s.includes("ticket"))
    return (
      <Ticket size={14} className={`${cls} text-puntt-pink-9`} aria-hidden />
    );
  if (s.includes("review history"))
    return (
      <ClockCounterClockwise
        size={14}
        className={`${cls} text-puntt-gold-11`}
        aria-hidden
      />
    );
  if (s.includes("regulatory") || s.includes("puntt"))
    return (
      <MagnifyingGlass
        size={14}
        className={`${cls} text-puntt-accent-9`}
        aria-hidden
      />
    );
  if (s.includes("sharepoint") || s.includes("claims"))
    return (
      <File size={14} className={`${cls} text-puntt-gold-9`} aria-hidden />
    );
  if (s.includes("code"))
    return (
      <File size={14} className={`${cls} text-puntt-accent-11`} aria-hidden />
    );
  return (
    <Globe size={14} className={`${cls} text-malachite-700`} aria-hidden />
  );
}

export function SearchProgress({ searchProgress }: SearchProgressProps) {
  const visibleProgress = searchProgress.filter(
    (s) =>
      s.status === "searching" ||
      (s.status === "done" &&
        ((s.resultCount ?? 0) > 0 || (s.results?.length ?? 0) > 0)),
  );

  if (visibleProgress.length === 0) return null;

  return (
    <div data-testid="ask-puntt-search-progress" className="space-y-1.5">
      {visibleProgress.map((source, idx) => {
        const isSearching = source.status === "searching";
        return (
          <Flex key={idx} className={TOOL_CHIP_CLASS}>
            {isSearching ? (
              <CircleNotch
                size={14}
                className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
                aria-hidden
              />
            ) : (
              <SourceIcon source={source.source} />
            )}
            <Text
              size="1"
              className={`flex-1 ${isSearching ? "text-puntt-neutral-gray-11" : "text-puntt-neutral-gray-12"}`}
            >
              {isSearching ? "Searching " : ""}
              {source.source}
            </Text>
            {!isSearching && source.resultCount !== undefined && (
              <Text size="1" className="text-puntt-neutral-gray-9">
                {source.resultCount}{" "}
                {source.resultCount === 1 ? "Result" : "Results"}
              </Text>
            )}
          </Flex>
        );
      })}
    </div>
  );
}
